import { AclVerb } from '@agilelab/plugin-wb-access-control-common';
import { InstanceEntity } from '@agilelab/plugin-wb-marketplace-common';
import { UserEntity } from '@agilelab/plugin-wb-platform';
import { alertApiRef, configApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  makeStyles,
  Theme,
  Typography,
  createStyles,
} from '@material-ui/core';
import { useCallback, useRef, useState } from 'react';

import { scaffolderApiRef } from '@agilelab/plugin-wb-scaffolder';
import { expandEntityRef } from '@agilelab/plugin-wb-platform-common';
import { stringifyEntityRef as stringifyRef } from '@mainstage/catalog-model';
import { identityApiRef } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';
import {
  AccessControlRequestDialog,
  cloneFormWithDescriptor,
} from './AccessRequestDialog';
import { TemplateGroup } from './hooks/resolveTemplateGroups';
import { useProductPendingRequestsLink } from '@agilelab/plugin-wb-access-control';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
    },
    groupList: {
      width: 160,
      flexShrink: 0,
      borderRight: `1px solid ${theme.palette.divider}`,
      paddingRight: theme.spacing(2),
      marginRight: theme.spacing(2),
    },
    groupItem: {
      cursor: 'default',
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.5, 1),
    },
    activeGroup: {
      fontWeight: theme.typography.fontWeightBold as any,
    },
    doneGroup: {
      color: theme.palette.text.disabled,
    },
    formArea: {
      flex: 1,
      minWidth: 0,
    },
  }),
);

type Props = {
  isOpen: boolean;
  groups: TemplateGroup[];
  systemInstance: InstanceEntity;
  userEntity: UserEntity;
  onRequestSent: () => void;
  setIsOpen: (value: boolean) => void;
  setScaffolderTaskIds: (taskIds: string[]) => void;
  verb: AclVerb;
  requestSource: 'system' | 'component';
};

/**
 * Orchestrates sequential Access Control Request dialogs when selected output ports
 * span multiple technology templates. The user fills one form per template group;
 * after the last group all scaffold calls are fired together.
 */
export const MultiTemplateAccessRequestDialog = ({
  isOpen,
  groups,
  systemInstance,
  userEntity,
  onRequestSent,
  setIsOpen,
  setScaffolderTaskIds,
  verb,
  requestSource,
}: Props) => {
  const classes = useStyles();
  const alertApi = useApi(alertApiRef);
  const configApi = useApi(configApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const consumableInterfaceTypeField = configApi.getString(
    'practiceShaper.migration.consumableInterfaceTypeField',
  );

  const { value: requesterRawEntity } = useAsync(async () => {
    const { token } = await identityApi.getCredentials();
    return catalogApi.getEntityByRef(expandEntityRef(userEntity.username), {
      token,
    });
  }, [catalogApi, identityApi, userEntity.username]);

  // Collected scaffold call factories per group, in order
  const pendingCallsRef = useRef<Array<() => Promise<{ taskId: string }>>>([]);
  // Number of calls pushed per group index (used to truncate when re-submitting after back)
  const pendingCallCountsRef = useRef<number[]>([]);
  // Saved form state per group index (used to restore when navigating back)
  const savedFormStatesRef = useRef<Record<number, Record<string, any>>>({});
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);

  const productPendingRequestsLink = useProductPendingRequestsLink(
    systemInstance.id,
  );

  const resetState = useCallback(() => {
    pendingCallsRef.current = [];
    pendingCallCountsRef.current = [];
    savedFormStatesRef.current = {};
    setCurrentGroupIndex(0);
  }, []);

  const handleCancel = useCallback(() => {
    resetState();
    setIsOpen(false);
  }, [resetState, setIsOpen]);

  const handleGroupNext = useCallback(
    async (formState: Record<string, any>) => {
      const group = groups[currentGroupIndex];
      const template = group.template;
      const templateRef = stringifyRef(template);

      // Save form state for this group
      savedFormStatesRef.current = {
        ...savedFormStatesRef.current,
        [currentGroupIndex]: formState,
      };

      // If the user has gone back and is re-submitting this group, truncate any
      // previously stored calls from this group onwards before pushing new ones.
      if (pendingCallCountsRef.current[currentGroupIndex] !== undefined) {
        const callsBeforeThisGroup = pendingCallCountsRef.current
          .slice(0, currentGroupIndex)
          .reduce((sum, n) => sum + n, 0);
        pendingCallsRef.current = pendingCallsRef.current.slice(
          0,
          callsBeforeThisGroup,
        );
        pendingCallCountsRef.current = pendingCallCountsRef.current.slice(
          0,
          currentGroupIndex,
        );
      }

      // Store scaffold calls for this group
      for (const outputPort of group.outputPorts) {
        pendingCallsRef.current.push(() =>
          scaffolderApi.scaffold({
            templateRef,
            values: cloneFormWithDescriptor(
              formState,
              outputPort,
              systemInstance,
              userEntity,
              requesterRawEntity,
              consumableInterfaceTypeField,
              productPendingRequestsLink,
            ),
          }),
        );
      }
      pendingCallCountsRef.current[currentGroupIndex] =
        group.outputPorts.length;

      const isLast = currentGroupIndex === groups.length - 1;
      if (isLast) {
        // Fire all collected requests
        try {
          const responses = await Promise.all(
            pendingCallsRef.current.map(fn => fn()),
          );
          onRequestSent();
          setScaffolderTaskIds(responses.map(r => r.taskId));
        } catch (err: any) {
          alertApi.post({ message: err.message, severity: 'error' });
        } finally {
          resetState();
          setIsOpen(false);
        }
      } else {
        setCurrentGroupIndex(idx => idx + 1);
      }
    },
    [
      currentGroupIndex,
      groups,
      scaffolderApi,
      systemInstance,
      userEntity,
      requesterRawEntity,
      consumableInterfaceTypeField,
      onRequestSent,
      setScaffolderTaskIds,
      alertApi,
      resetState,
      setIsOpen,
      productPendingRequestsLink,
    ],
  );

  const handleGroupBack = useCallback(() => {
    setCurrentGroupIndex(idx => idx - 1);
  }, []);

  if (!isOpen || groups.length === 0) return null;

  const currentGroup = groups[currentGroupIndex];
  const isLastGroup = currentGroupIndex === groups.length - 1;

  const GroupList = () => (
    <Box className={classes.groupList}>
      <List dense disablePadding>
        {groups.map((group, idx) => {
          const label =
            group.technologyName ??
            group.template.metadata.title ??
            group.template.metadata.name;
          const isActive = idx === currentGroupIndex;
          const isDone = idx < currentGroupIndex;
          let groupClassName: string | undefined;
          if (isActive) groupClassName = classes.activeGroup;
          else if (isDone) groupClassName = classes.doneGroup;
          return (
            <ListItem key={idx} className={classes.groupItem} disableGutters>
              <ListItemText
                primary={
                  <Typography variant="body2" className={groupClassName}>
                    {label}
                  </Typography>
                }
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <AccessControlRequestDialog
      key={currentGroupIndex}
      isOpen={isOpen}
      template={currentGroup.template}
      systemInstance={systemInstance}
      selectedOutputPorts={currentGroup.outputPorts}
      userEntity={userEntity}
      onRequestSent={onRequestSent}
      setIsOpen={handleCancel}
      setScaffolderTaskIds={setScaffolderTaskIds}
      verb={verb}
      requestSource={requestSource}
      onNext={handleGroupNext}
      onBack={currentGroupIndex > 0 ? handleGroupBack : undefined}
      initialFormData={savedFormStatesRef.current[currentGroupIndex]}
      submitLabel={isLastGroup ? 'Send' : 'Next'}
      groupListSlot={groups.length > 1 ? <GroupList /> : undefined}
    />
  );
};
